import { useMemo } from "react";
import { DashboardRow, HeadCell, SetBarChartData, SetBarChartProps } from "../types/dashboard";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const useSetBarChartData = (dashboardData: SetBarChartProps["dashboardData"], selectedYear: number): SetBarChartData => {
  const { orders, sales, discount, deliveryFee, parcelFee, tip, received, outstanding } = useMemo(() => {
    const initialData = {
      orders: Array(12).fill(0),
      sales: Array(12).fill(0),
      discount: Array(12).fill(0),
      deliveryFee: Array(12).fill(0),
      parcelFee: Array(12).fill(0),
      tip: Array(12).fill(0),
      outstanding: Array(12).fill(0),
      received: Array(12).fill(0),
    };

    const monthlyData = dashboardData?.orders_summary || [];
    const calculateMonthlyData = dashboardData?.calculate_monthly_summary || [];

    monthlyData.forEach((summary) => {
      const monthIndex = MONTH_LABELS.indexOf(summary.month);
      if (monthIndex !== -1 && summary.year === selectedYear) {
        initialData.orders[monthIndex] = summary.totalOrders || 0;
        initialData.sales[monthIndex] = summary.totalSales || 0;
        initialData.discount[monthIndex] = summary.combinedDiscount || 0;
        initialData.deliveryFee[monthIndex] = summary.totalDeliveryFee || 0;
        initialData.parcelFee[monthIndex] = summary.totalParcelFee || 0;
        initialData.tip[monthIndex] = summary.totalTip || 0;
        initialData.outstanding[monthIndex] = summary.totalOutstandingBalance || 0;
      }
    });

    calculateMonthlyData.forEach((summary) => {
      const monthIndex = MONTH_LABELS.indexOf(summary.month);
      if (monthIndex !== -1 && summary.year === selectedYear) {
        initialData.received[monthIndex] = summary.total_received || 0;
      }
    });

    return initialData;
  }, [dashboardData, selectedYear]);

  const tableRows: DashboardRow[] = useMemo( () => MONTH_LABELS.map((month, index) => ({
        id: index + 1,
        month,
        totalOrders: orders[index],
        totalSales: sales[index].toFixed(2),
        combinedDiscount: discount[index].toFixed(2),
        totalDeliveryFee: deliveryFee[index].toFixed(2),
        totalParcelFee: parcelFee[index].toFixed(2),
        totalTip : tip[index].toFixed(2),
        receivedAmount: received[index].toFixed(2),
        outstandingBalance: outstanding[index].toFixed(2),
      })),
    [orders, sales, discount, deliveryFee, parcelFee, tip, received, outstanding]
  );

  const headCells: HeadCell<DashboardRow>[] = useMemo( () => [
      { id: "id", label: "S.No", numeric: false },
      { id: "month", label: "Months", numeric: false },
      { id: "totalOrders", label: "Total Orders", numeric: true, align: "center" },
      { id: "totalSales", label: "Total Sales (QAR)", numeric: true, align: "right" },
      { id: "combinedDiscount", label: "Total Discount (QAR)", numeric: true, align: "right" },
      { id: "totalDeliveryFee", label: "Total Delivery Fee (QAR)", numeric: true, align: "right" },
      { id: "totalParcelFee", label: "Total Parcel Fee (QAR)", numeric: true, align: "right" },
      { id: "totalTip", label: "Total Tip (QAR)", numeric: true, align: "right" },
      { id: "receivedAmount", label: "Received Amount (QAR)", numeric: true, align: "right" },
      { id: "outstandingBalance", label: "Outstanding Balance (QAR)", numeric: true, align: "right" },
    ], [] );

  return {
    orders,
    sales,
    discount,
    received,
    outstanding,
    tableRows,
    headCells,
  };
};
